import { normalizeHexColor } from '../StoreCheckoutColorPicker/storeCheckoutColorPicker.utils';
import {
  STORE_CHECKOUT_BRANDING,
  STORE_CHECKOUT_CUSTOM_BRANDING_SWATCHES,
  STORE_LOGO_SOURCE,
  STORE_PRIMARY_COLOR_SOURCE,
} from '../constants/storeCheckout.constants';
import type { IDesignStoreFormShape } from '../StoreDetails/storeDetails.types';
import type { IStoreCustomizationBrandingState } from './storeCheckoutCustomizationPayload.utils';
import { isStorePrimaryColorSource } from './storeCheckoutCustomizationPayload.utils';
import type {
  IStoreResponse,
  StoreLogoSource,
  StorePrimaryColorSource,
} from '@/common/interfaces/store.interface';
import { s3FileUrlGetter } from '@/utilities/CommonUtils';

type IStoreBrandingApiFields = IStoreResponse & {
  primaryColor?: string | null;
  primaryColorSource?: string | null;
  customHexColor?: string | null;
  storeLogoCmsUrl?: string | null;
  storeLogoFile?: string | null;
  storeLogoUrl?: string | null;
  storeLogoSource?: string | null;
};

const readStoreBrandingString = (
  store: IStoreBrandingApiFields,
  snakeCaseKey: keyof IStoreBrandingApiFields,
  camelCaseKey: keyof IStoreBrandingApiFields
): string | null => {
  const value = store[snakeCaseKey] ?? store[camelCaseKey];

  if (value == null || value === '') {
    return null;
  }

  return String(value);
};

const resolveStoreLogoSource = (store: IStoreBrandingApiFields): StoreLogoSource => {
  const raw = readStoreBrandingString(store, 'store_logo_source', 'storeLogoSource');

  return raw === STORE_LOGO_SOURCE.UPLOADED
    ? STORE_LOGO_SOURCE.UPLOADED
    : STORE_LOGO_SOURCE.DEFAULT;
};

const resolvePresetSwatchColor = (hex: string): string => {
  const normalized = normalizeHexColor(hex);

  if (!normalized) {
    return hex;
  }

  const preset = STORE_CHECKOUT_CUSTOM_BRANDING_SWATCHES.find(
    (swatch) => normalizeHexColor(swatch)?.toUpperCase() === normalized.toUpperCase()
  );

  return preset ?? normalized;
};

const extractDesignsFolderFileName = (url: string): string | null => {
  const match = url.match(/\/designs\/([^?#]+)/i);

  return match?.[1] ?? null;
};

const resolveStoreLogoUrl = (
  store: IStoreBrandingApiFields,
  storeLogoFileName: string | null
): string | null => {
  if (storeLogoFileName) {
    return s3FileUrlGetter(storeLogoFileName);
  }

  const directUrl =
    readStoreBrandingString(store, 'store_logo_url', 'storeLogoUrl') ??
    readStoreBrandingString(store, 'store_logo_cms_url', 'storeLogoCmsUrl');

  if (!directUrl) {
    return null;
  }

  const fileNameFromUrl = extractDesignsFolderFileName(directUrl);

  return fileNameFromUrl ? s3FileUrlGetter(fileNameFromUrl) : directUrl;
};

export const buildStoreBrandingStateFromStore = (
  store: IStoreResponse
): IStoreCustomizationBrandingState | null => {
  const brandingStore = store as IStoreBrandingApiFields;
  const primaryColorRaw = readStoreBrandingString(brandingStore, 'primary_color', 'primaryColor');
  const primaryColor = primaryColorRaw ? resolvePresetSwatchColor(primaryColorRaw) : null;

  if (!primaryColor) {
    return null;
  }

  const primaryColorSourceRaw = readStoreBrandingString(
    brandingStore,
    'primary_color_source',
    'primaryColorSource'
  );
  const primaryColorSource: StorePrimaryColorSource =
    primaryColorSourceRaw != null && isStorePrimaryColorSource(primaryColorSourceRaw)
      ? primaryColorSourceRaw
      : STORE_PRIMARY_COLOR_SOURCE.PRESET;

  const customHexColor = normalizeHexColor(
    readStoreBrandingString(brandingStore, 'custom_hex_color', 'customHexColor')
  );
  const customColors =
    primaryColorSource === STORE_PRIMARY_COLOR_SOURCE.CUSTOM && customHexColor
      ? [customHexColor]
      : [];

  const storeLogoSource = resolveStoreLogoSource(brandingStore);
  const storeLogoFileName = readStoreBrandingString(
    brandingStore,
    'store_logo_file',
    'storeLogoFile'
  );

  return {
    selectedColor: primaryColor,
    primaryColorSource,
    customColors,
    storeLogoFileName,
    storeLogoSource,
  };
};

export const buildDesignStoreFormValuesFromStore = (
  store: IStoreResponse
): Partial<IDesignStoreFormShape> => {
  const branding = buildStoreBrandingStateFromStore(store);

  if (!branding) {
    return {};
  }

  const brandingStore = store as IStoreBrandingApiFields;
  const uploadedStoreLogoUrl =
    branding.storeLogoSource === STORE_LOGO_SOURCE.UPLOADED
      ? resolveStoreLogoUrl(brandingStore, branding.storeLogoFileName)
      : null;
  const storeLogo = uploadedStoreLogoUrl ?? STORE_CHECKOUT_BRANDING.DEFAULT_FP_LOGO_SRC;

  return {
    selectedColor: branding.selectedColor,
    storeLogo,
  };
};

export const buildBrandingStateForEditSave = (
  designStore: IDesignStoreFormShape,
  savedBranding: IStoreCustomizationBrandingState | null,
  overrideBranding?: IStoreCustomizationBrandingState | null
): IStoreCustomizationBrandingState => {
  if (overrideBranding) {
    return overrideBranding;
  }

  return {
    selectedColor: designStore.selectedColor ?? savedBranding?.selectedColor ?? '',
    primaryColorSource: savedBranding?.primaryColorSource ?? STORE_PRIMARY_COLOR_SOURCE.PRESET,
    customColors:
      designStore.selectedColors.length > 0
        ? designStore.selectedColors
        : savedBranding?.customColors ?? [],
    storeLogoFileName: savedBranding?.storeLogoFileName ?? null,
    storeLogoSource: savedBranding?.storeLogoSource ?? STORE_LOGO_SOURCE.DEFAULT,
  };
};
